import { Flex, Input, Text } from '@chakra-ui/react'
import React, { useEffect, useId, useState } from 'react'

import {
  addDays,
  getEndOfDay,
  getStartOfDay,
  TimePeriod,
  tryGetMergedTimePeriodDate,
  tryGetTimePeriod,
} from '@mangovoice/core'
import { TimePeriodRange as CoreTimePeriodRange } from '@mangovoice/core'
import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import { useReportingI18n } from '../i18n'
import { DateInput } from './DateInput'
import { DateRange, TimePeriodRange } from './DateRange'
import { useKeyPresses } from './hooks/useKeyPresses'
import { MangoButton, MangoButtonVariant } from './MangoButton'
import { MangoModal } from './MangoModal'
import { TimeInput } from './TimeInput'

enum PeriodName {
  After = 'after',
  Before = 'before',
}

interface PeriodGroupProps {
  label: string
  optionalText: string
  children: React.ReactNode
}

const PeriodGroup = ({ label, optionalText, children }: PeriodGroupProps) => {
  const labelId = useId()
  const optionalId = useId()

  return (
    <Flex
      role="group"
      aria-labelledby={labelId}
      aria-describedby={optionalId}
      direction="column"
    >
      <Flex alignSelf="stretch" alignItems="center" gap="1.25rem">
        <Text {...textStyles[textStyleKeys.Body2_Bold]}>
          <span id={labelId}>{label}</span>{' '}
          <Text as="span" id={optionalId} {...textStyles[textStyleKeys.Body2]}>
            {optionalText}
          </Text>
        </Text>
      </Flex>
      <Flex alignSelf="stretch" gap="1.25rem" py="0.5rem" alignItems="center">
        {children}
      </Flex>
    </Flex>
  )
}

// A midnight Before reads as a date-only bound wherever ranges are loaded
// from URLs, so an exclusive 12:00 AM is stored as the end of the previous day.
const getBeforeDate = (date?: Date, timePeriod?: TimePeriod) => {
  if (!date) return undefined
  if (!timePeriod) return getEndOfDay(date)

  const merged = tryGetMergedTimePeriodDate(date, timePeriod)!
  return merged.getTime() === getStartOfDay(merged).getTime()
    ? getEndOfDay(addDays(merged, -1))
    : merged
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0')

const toDateInputValue = (date?: Date) =>
  date
    ? `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    : ''

const MIN_YEAR = 1000

// Browsers report a half-typed year ("0002-10-13") on every keystroke; only a
// four-digit year is a date, and setFullYear keeps years under 100 as typed.
const fromDateInputValue = (value: string) => {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day || year < MIN_YEAR) return undefined
  const date = new Date(2000, month - 1, day)
  date.setFullYear(year)
  return date
}

interface CalendarDateInputProps {
  label: string
  value?: Date
  min?: Date
  max?: Date
  mountsFocused?: boolean
  onDateSet: (date?: Date) => void
}

const CalendarDateInput = ({
  label,
  value,
  min,
  max,
  mountsFocused,
  onDateSet,
}: CalendarDateInputProps) => {
  const [raw, setRaw] = useState(() => toDateInputValue(value))

  useEffect(() => {
    if (fromDateInputValue(raw)?.getTime() !== value?.getTime())
      setRaw(toDateInputValue(value))
  }, [value])

  return (
    <Input
      type="date"
      aria-label={label}
      value={raw}
      min={toDateInputValue(min) || undefined}
      max={toDateInputValue(max) || undefined}
      autoFocus={mountsFocused}
      px="0.75rem"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setRaw(e.target.value)
        onDateSet(fromDateInputValue(e.target.value))
      }}
    />
  )
}

interface DateRangeModalProps {
  isOpen?: boolean
  dateTimeRange?: DateRange
  /** Pick whole days from a calendar: After starts its day, Before ends its day. */
  dateOnly?: boolean
  onClose?: () => void
  onClear?: () => void
  onSet?: (dateTimeRange: DateRange) => void
}

export const DateTimeRangeModal = ({
  isOpen,
  dateTimeRange: externalDateTimeRange,
  dateOnly = false,
  onClose,
  onClear,
  onSet,
}: DateRangeModalProps) => {
  const { text } = useReportingI18n()
  const [dateRange, setDateRange] = useState<DateRange>(
    externalDateTimeRange ?? DateRange.empty(),
  )

  const [timeRange, setTimeRange] = useState<CoreTimePeriodRange>(
    TimePeriodRange.empty,
  )

  const setTimeRangeValue = (
    periodName: PeriodName,
    timePeriod?: TimePeriod,
  ) => {
    const newTimeRange: CoreTimePeriodRange = { ...timeRange }
    newTimeRange[periodName] = timePeriod

    setTimeRange(newTimeRange)
  }

  const setDateRangeValue = (period: PeriodName, value: Date | undefined) => {
    const newDateRange: DateRange = { ...dateRange }
    newDateRange[period] = value

    setDateRange(newDateRange)
  }

  const isReversed =
    dateOnly &&
    !!dateRange.after &&
    !!dateRange.before &&
    getStartOfDay(dateRange.after) > getStartOfDay(dateRange.before)

  const handleOnSet = () => {
    if (isReversed) return
    if (dateOnly) {
      onSet?.({
        after: dateRange.after && getStartOfDay(dateRange.after),
        before: dateRange.before && getEndOfDay(dateRange.before),
      })
      onClose?.()
      return
    }

    const newAfterDate = tryGetMergedTimePeriodDate(
      dateRange.after,
      timeRange.after,
    )

    onSet?.({
      after: newAfterDate,
      before: getBeforeDate(dateRange.before, timeRange.before),
    })

    onClose?.()
  }

  useEffect(() => {
    if (externalDateTimeRange) {
      const timePeriodRange = {
        before: tryGetTimePeriod(externalDateTimeRange.before),
        after: tryGetTimePeriod(externalDateTimeRange.after),
      }

      setTimeRange(timePeriodRange)
      setDateRange({ ...externalDateTimeRange })
    }
  }, [
    externalDateTimeRange,
    externalDateTimeRange?.before,
    externalDateTimeRange?.after,
  ])

  useKeyPresses({
    onPress: (e: KeyboardEvent) => isOpen && e.key === 'Enter' && handleOnSet(),
  })

  const afterDate = dateRange?.after
  const afterTime = timeRange?.after

  const beforeDate = dateRange?.before
  const beforeTime = timeRange?.before

  const afterLabel = text('dateTimeRangeModal.after', 'After').trim()
  const beforeLabel = text('dateTimeRangeModal.before', 'Before').trim()
  const optionalText = text('dateTimeRangeModal.optional', '(optional)').trim()

  return (
    <MangoModal
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose?.()
      }}
      header={text('dateTimeRangeModal.selectDateRange', 'Select Date Range')}
      body={
        dateOnly ? (
          <Flex direction="column">
            <PeriodGroup label={afterLabel} optionalText={optionalText}>
              <CalendarDateInput
                label={afterLabel}
                value={afterDate}
                max={beforeDate}
                mountsFocused
                onDateSet={(val) => setDateRangeValue(PeriodName.After, val)}
              />
            </PeriodGroup>
            <PeriodGroup label={beforeLabel} optionalText={optionalText}>
              <CalendarDateInput
                label={beforeLabel}
                value={beforeDate}
                min={afterDate}
                onDateSet={(val) => setDateRangeValue(PeriodName.Before, val)}
              />
            </PeriodGroup>
          </Flex>
        ) : (
          <Flex direction="column">
            <PeriodGroup label={afterLabel} optionalText={optionalText}>
              <DateInput
                label={afterLabel}
                value={afterDate}
                mountsFocused
                onDateSet={(val) => setDateRangeValue(PeriodName.After, val)}
              />
              <TimeInput
                label={afterLabel}
                timePeriod={afterTime}
                disabled={!afterDate}
                onTimeSet={(timePeriod) =>
                  setTimeRangeValue(PeriodName.After, timePeriod)
                }
              />
            </PeriodGroup>
            <PeriodGroup label={beforeLabel} optionalText={optionalText}>
              <DateInput
                label={beforeLabel}
                value={beforeDate}
                onDateSet={(val) => setDateRangeValue(PeriodName.Before, val)}
              />
              <TimeInput
                label={beforeLabel}
                timePeriod={beforeTime}
                disabled={!beforeDate}
                onTimeSet={(timePeriod) =>
                  setTimeRangeValue(PeriodName.Before, timePeriod)
                }
              />
            </PeriodGroup>
          </Flex>
        )
      }
      footer={
        <Flex gap="0.75rem">
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={onClear}
          >
            {text('dateTimeRangeModal.clearButton', 'Clear')}
          </MangoButton>
          <MangoButton
            type="submit"
            variant={MangoButtonVariant.Default_300}
            disabled={isReversed}
            onClick={handleOnSet}
          >
            {text('dateTimeRangeModal.setDateRangeButton', 'Set Date Range')}
          </MangoButton>
        </Flex>
      }
    />
  )
}
